// pages/CheckoutPage.js
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';

import ContactForm from '../components/checkout/ContactForm';
import SupplierProductSection from '../components/checkout/SupplierProductSection';
import SupplierSummary from '../components/checkout/SupplierSummary';
import StripeCardInput from '../components/checkout/StripeCardInput';

import useUserProfile from '../hooks/useUserProfile';
import useSuppliers from '../hooks/useSuppliers';
import useGroupedCartItems from '../hooks/useGroupedCartItems';
import handleCheckout from '../utils/handleCheckout';

import './CheckoutPage.css';
// SEO: lisää tämä import
import Seo from '../components/seo/SeoHead';

/* ----------------------------- API helper ----------------------------- */
const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const apiUrl = (p) => (API_BASE ? `${API_BASE}${p}` : p);

/* ----------------------------- Helpers hero-summalle ----------------------------- */
const VAT_RATE = 0.255; // 25,5 %
const eur = (n) => `${(Number(n) || 0).toFixed(2)} €`;
const pct = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n <= 1 ? n * 100 : n;
};

function computeSupplierFinal({ items = [], supplier = {}, deliveryType, isRecurring }) {
  const subtotal = items.reduce(
    (s, it) => s + Number(it?.product?.price || 0) * Number(it?.quantity || 0),
    0
  );
  const isGroup = deliveryType === 'group';
  const basePct = isGroup ? pct(supplier?.discountForGroupOrders ?? 0.02) : 0;
  const extraPct = isGroup && isRecurring ? 2 : 0;
  const discountAmount = (subtotal * (basePct + extraPct)) / 100;
  const after = Math.max(subtotal - discountAmount, 0);
  const freeTh = Number(supplier?.freeDeliveryThreshold ?? Infinity);
  const feeBase = Number(supplier?.deliveryFee ?? 0);
  const fee = after >= freeTh ? 0 : feeBase;

  const final = after + fee;              // veroton
  const vat = final * VAT_RATE;           // ALV 25,5 %
  const finalInclVat = final + vat;       // sis. ALV

  return { subtotal, discountAmount, fee, final, vat, finalInclVat };
}

const CheckoutPage = () => {
  const { cartItems, updateQuantity, clearCart } = useCart();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = encodeURIComponent(`${location.pathname}${location.search || ''}` || '/checkout');
  const stripe = useStripe();
  const elements = useElements();

  // Ostoskori, jota checkout käyttää näyttöön ja summiin (voidaan päivittää hinnan tarkistuksessa)
  const [effectiveCartItems, setEffectiveCartItems] = useState(cartItems);

  // Guest-checkout sallitaan, mutta recurring voi vaatia kirjautumisen (hoituu handleCheckoutissa)
  const [isGuestCheckout, setIsGuestCheckout] = useState(false);

  const [marketingConsent, setMarketingConsent] = useState(false);
  const [gdprConsent, setGdprConsent] = useState(false);

  // Submitin tila (estää tuplaklikkauksen + näyttää spinnerin)
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Toimittajakohtainen toimitustapa (group | immediate)
  const [deliveryTypeBySupplier, setDeliveryTypeBySupplier] = useState({});
  // Toimittajakohtaiset toistuvan tilauksen asetukset
  const [recurringSettingsBySupplier, setRecurringSettingsBySupplier] = useState({}); // { supplierId: { isRecurring:boolean, frequency:number } }

  const {
    fullName, setFullName,
    email, setEmail,
    phone, setPhone,
    // eritelty toimitusosoite
    streetAddress, setStreetAddress,
    postalCode, setPostalCode,
    city, setCity,
    businessId, setBusinessId,
  } = useUserProfile(user);

  // Pidä effectiveCartItems synkassa CartContextin kanssa, ellei hinnantarkistus ole juuri päivittänyt hintoja
  useEffect(() => {
    setEffectiveCartItems(cartItems);
  }, [cartItems]);

  const { suppliers } = useSuppliers(user, isGuestCheckout, effectiveCartItems);
  const supplierMap = useGroupedCartItems(effectiveCartItems);

  // ✅ Älä resetoi käyttäjän valitsemaa toimitustapaa, kun kori muuttuu
  useEffect(() => {
    setDeliveryTypeBySupplier((prev) => {
      const next = { ...prev };
      // lisää oletus 'group' vain uusille toimittajille
      Object.keys(supplierMap).forEach((supplierId) => {
        if (next[supplierId] == null) next[supplierId] = 'group';
      });
      // siivoa poistuneet toimittajat
      Object.keys(next).forEach((supplierId) => {
        if (!supplierMap[supplierId]) delete next[supplierId];
      });
      return next;
    });
  }, [supplierMap]);

  if (!user && !isGuestCheckout) {
    return (
      <div className="checkout-guest-prompt">
        <h2>Jatka tilausta</h2>
        <p>Voit kirjautua sisään tai jatkaa ilman kirjautumista.</p>
        <button onClick={() => navigate(`/login?redirect=${redirect}`)} className="checkout-login-button">
          Kirjaudu sisään
        </button>
        <button onClick={() => setIsGuestCheckout(true)} className="checkout-guest-button">
          Jatka ilman kirjautumista
        </button>
      </div>
    );
  }

  if (cartItems.length === 0 || Object.keys(suppliers).length === 0) {
    return <div className="checkout-loading">Ladataan kassaa...</div>;
  }

  // Koosta sections hero-summalle
  const sections = Object.entries(supplierMap).map(([supplierId, items]) => ([
    supplierId,
    {
      supplier: suppliers[supplierId],
      items,
      deliveryType: deliveryTypeBySupplier[supplierId],
      isRecurring: !!(recurringSettingsBySupplier[supplierId]?.isRecurring),
    }
  ]));

  const heroAgg = sections.reduce(
    (acc, [, s]) => {
      const r = computeSupplierFinal(s);
      acc.subtotal += r.subtotal;
      acc.discount += r.discountAmount;
      acc.delivery += r.fee;
      acc.net      += r.final;         // veroton
      acc.vat      += r.vat;           // ALV
      acc.gross    += r.finalInclVat;  // sis. ALV
      return acc;
    },
    { subtotal: 0, discount: 0, delivery: 0, net: 0, vat: 0, gross: 0 }
  );

  // ----------------------------- Hinnan tarkistus ennen checkoutia ----------------------------- //
  const verifyAndSyncPrices = async () => {
    try {
      // Kerää uniikit tuote-id:t ja nykyiset hinnat
      const byId = {};
      effectiveCartItems.forEach((item) => {
        const id = item?.product?._id;
        if (!id) return;
        if (!byId[id]) {
          byId[id] = {
            name: item.product?.name || '',
            currentPrice: Number(item.product?.price || 0),
          };
        }
      });

      const ids = Object.keys(byId);
      if (!ids.length) return true;

      const changes = [];

      await Promise.all(
        ids.map(async (id) => {
          try {
            const resp = await axios.get(apiUrl(`/api/products/${id}`));
            const backendProduct = resp?.data?.product || resp?.data;
            if (!backendProduct) return;

            const latestPrice = Number(backendProduct.price || 0);
            const currentPrice = byId[id].currentPrice;

            if (Number.isFinite(latestPrice) && latestPrice !== currentPrice) {
              changes.push({
                id,
                name: backendProduct.name || byId[id].name || 'Tuote',
                oldPrice: currentPrice,
                newPrice: latestPrice,
              });
            }
          } catch (err) {
            console.error('Hinnan tarkistus epäonnistui tuotteelle', id, err);
          }
        })
      );

      if (!changes.length) {
        // Kaikki hinnat ovat samat kuin backendissä
        return true;
      }

      const lines = changes.map((c) =>
        `• ${c.name}: ${eur(c.oldPrice)} → ${eur(c.newPrice)}`
      );

      const confirmed = window.confirm(
        `Tuotteiden hinnat ovat päivittyneet sillä aikaa, kun olet ollut kassalla:\n\n${lines.join(
          '\n'
        )}\n\nHaluatko jatkaa tilausta uusilla hinnoilla?`
      );

      if (!confirmed) {
        // Käyttäjä ei hyväksynyt uusia hintoja → ei jatketa checkoutia
        return false;
      }

      // Päivitä checkoutin käyttämä ostoskori uusilla hinnoilla näkyviin
      const changedMap = changes.reduce((acc, c) => {
        acc[c.id] = c;
        return acc;
      }, {});

      setEffectiveCartItems((prev) =>
        prev.map((item) => {
          const id = item?.product?._id;
          if (!id || !changedMap[id]) return item;
          return {
            ...item,
            product: {
              ...item.product,
              price: changedMap[id].newPrice,
            },
          };
        })
      );

      return true;
    } catch (err) {
      console.error('Hinnan tarkistus epäonnistui', err);
      // Fallback: ei blokata tilausta kokonaan, mutta logitetaan virhe
      return true;
    }
  };

  // ----------------------------- Submit ----------------------------- //
  const onSubmit = async (e) => {
    // Estä oletuslähetys heti (ennen asynkronisia operaatioita)
    e.preventDefault();

    if (isSubmitting) {
      // Jos nappia on jo painettu, ei tehdä mitään (estää tuplaklikkauksen)
      return;
    }

    setIsSubmitting(true);

    try {
      // 1) Varmistetaan, että hinnat vastaavat backendin tämänhetkisiä arvoja
      const ok = await verifyAndSyncPrices();
      if (!ok) {
        // Käyttäjä ei hyväksynyt uusia hintoja → perutaan
        return;
      }

      // 2) Jatketaan normaalia checkoutia
      //    Annetaan handleCheckoutille "feikki-event", jotta se voi kutsua e.preventDefault()
      const fakeEvent = { preventDefault: () => {} };

      await handleCheckout({
        e: fakeEvent,
        stripe,
        elements,
        user,
        supplierMap,
        deliveryTypeBySupplier,
        recurringSettingsBySupplier,
        cartItems: effectiveCartItems, // käytä hinnantarkistuksen päivittämää ostoskoria
        fullName,
        email,
        phone,
        // uudet eritellyt osoitetiedot
        streetAddress,
        postalCode,
        city,
        businessId,
        marketingConsent,
        gdprConsent,
        clearCart,
        navigate,
      });
    } finally {
      // Varmistetaan, että nappi aktivoituu uudelleen myös virhetilanteissa
      setIsSubmitting(false);
    }
  };

  return (
    <div className="checkout-page">
      {/* SEO: noindex + perusmetat */}
      <Seo
        title="Kassa | Fourplify"
        description="Turvallinen maksaminen yrityksille."
        canonical="https://www.fourplify.com/checkout"
        noindex
      />

      <div className="checkout-title-wrapper">
        <h1 className="checkout-title">Kassa</h1>
        <div className="checkout-title-underline" />
        <p className="checkout-subtitle">Hinnat sisältävät ALV-erittelyn (25,5 %).</p>
      </div>

      <div className="checkout-container">
        <form className="checkout-card" onSubmit={onSubmit}>
          {/* Yhteystiedot */}
          <ContactForm
            {...{
              fullName, setFullName,
              email, setEmail,
              phone, setPhone,
              streetAddress, setStreetAddress,
              postalCode, setPostalCode,
              city, setCity,
              businessId, setBusinessId,
              marketingConsent, setMarketingConsent,
              gdprConsent, setGdprConsent,
            }}
          />

          {/* Tuotelistat toimittajittain */}
          <h2>Tuotteet</h2>
          {sections.map(([supplierId, s]) => (
            <SupplierProductSection
              key={supplierId}
              supplierId={supplierId}
              items={s.items}
              supplier={s.supplier}
              deliveryType={s.deliveryType}
              onDeliveryTypeChange={(type) =>
                setDeliveryTypeBySupplier((prev) => ({ ...prev, [supplierId]: type }))
              }
              recurringSettings={
                recurringSettingsBySupplier[supplierId] || { isRecurring: false, frequency: 4 }
              }
              onRecurringSettingsChange={(newSettings) =>
                setRecurringSettingsBySupplier((prev) => ({
                  ...prev,
                  [supplierId]: newSettings,
                }))
              }
              updateQuantity={updateQuantity}
            />
          ))}

          {/* Yhteenveto (sticky, hero-summa + säästöt + erittely heti alle) */}
          <div className="order-summary" id="yhteenveto">
            <h3>Yhteenveto</h3>

            {/* Hero-summa (sis. ALV) */}
            <div className="summary-hero">
              <div className="summary-hero-label">Yhteensä (sis. ALV)</div>
              <div className="summary-hero-amount">{eur(heroAgg.gross)}</div>
            </div>
            <div className="summary-subrows" style={{ marginTop: 6 }}>
              <div className="summary-row" style={{ display:'flex', justifyContent:'space-between', fontSize:13 }}>
                <span>Yhteensä (veroton)</span>
                <span>{eur(heroAgg.net)}</span>
              </div>
              <div className="summary-row" style={{ display:'flex', justifyContent:'space-between', fontSize:13 }}>
                <span>ALV 25,5 %</span>
                <span>{eur(heroAgg.vat)}</span>
              </div>
            </div>

            {/* Säästöt heti esiin – markkinointikulma */}
            {heroAgg.discount > 0 && (
              <div className="summary-savings">
                Säästät yhteensä <b>{eur(heroAgg.discount)}</b> Fourplifyn yhteisöllisellä tilaustavalla ja alennuksilla.
              </div>
            )}

            {/* Erittely heti Yhteensä-rivin alle (omalla scrollilla) */}
            <details className="summary-accordion" style={{ marginTop: 10 }}>
              <summary>Erittely</summary>
              <div
                className="summary-scroll"
                style={{ maxHeight: 320, overflowY: 'auto', paddingRight: 6 }}
              >
                {sections.map(([supplierId, s]) => (
                  <SupplierSummary
                    key={supplierId}
                    supplierId={supplierId}
                    items={s.items}
                    supplier={s.supplier}
                    deliveryType={s.deliveryType}
                    isRecurring={!!s.isRecurring}
                  />
                ))}
                <div className="small muted" style={{ marginTop: 6 }}>
                  Erittely sisältää verottoman summan, ALV 25,5 % sekä verollisen loppusumman.
                </div>
              </div>
            </details>

            {/* Maksukenttä + CTA */}
            <StripeCardInput />

            <div className="card-element-helper">
              <small>
                <b>Normaalitilauksessa</b> teemme kortille katevarauksen ja vahvistamme saatavuuden 24 h kuluessa. 
                Veloitus tehdään vasta, kun tuotteet on vahvistettu ja toimitus lähtee eteenpäin.
                <br />
                <b>Säästötilaus</b> on sitova heti ja veloitetaan sunnuntaina klo 23:50, kun erä lähetetään toimittajille.
                <br />
                <b>Toistuva tilaus</b> lähetetään valitsemallasi jaksolla (1-12 viikkoa), aina aktiivisen viikon sunnuntaina klo 23:50.
              </small>
            </div>

            <button
              type="submit"
              className="confirm-button"
              disabled={
                isSubmitting || // 🔹 estää tuplaklikkauksen
                !gdprConsent ||
                !stripe ||
                !elements?.getElement(CardElement)
              }
            >
              {isSubmitting ? (
                <>
                  <span className="confirm-button__spinner" aria-hidden="true" />
                  <span className="confirm-button__text">Käsitellään tilausta...</span>
                </>
              ) : (
                <span className="confirm-button__text">Vahvista tilaus</span>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Mobile FAB: scrollaa yhteenvetoon (desktopilla piilotettu CSS:llä) */}
      <button
        type="button"
        className="fab-summary"
        onClick={() =>
          document.getElementById('yhteenveto')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
        aria-label="Siirry yhteenvetoon"
      >
        <span className="dot" />
        Yhteenveto
      </button>
    </div>
  );
};

export default CheckoutPage;
